// bidHealth.js — per-session demand / bid health monitor.
//
// Answers at a glance: of N GAM ad requests, how many did Prebid win, how many
// did Amazon APS win, how many went to Google AdX, and how much demand simply
// no-bid. Returns a severity flag so a tester instantly sees whether header
// bidding is healthy or something is wrong.
//
// Pure function — no React, no imports. Safe to call from the dashboard, the
// sign-off report, or a headless test.
//
// Win attribution per GAM slot (from cust_params on the GAM request URL):
//   hb_bidder | hb_pb | hb_cache_id  → Prebid won
//   amzn_b    | amznslots            → Amazon APS won
//   neither, but a creative filled   → Google AdX won
//   noFill                            → no fill

function rowType(r) {
  return (r.type || r.requestType || '').toLowerCase();
}

function custParams(url) {
  try {
    const raw = new URL(url).searchParams.get('cust_params') || '';
    return Object.fromEntries(new URLSearchParams(decodeURIComponent(raw)));
  } catch {
    return {};
  }
}

export function computeBidHealth(rows = []) {
  const gam = rows.filter(
    (r) =>
      rowType(r) === 'google' &&
      String(r.validData?.adUnit || r.id || '').split('/').filter(Boolean).length >= 2
  );
  const prebid = rows.filter((r) => rowType(r) === 'prebid');
  const aps = rows.filter((r) => rowType(r) === 'amazon');

  let prebidWon = 0;   // RENDERED winners (from creativeSource), not just bid-passed
  let apsWon = 0;
  let adxWon = 0;
  let directWon = 0;
  let noFill = 0;
  let hbPassed = 0;     // slots where an HB bid was passed to GAM (cust_params)
  let hbPassedLost = 0; // of those, a non-HB creative rendered → HB lost the GAM auction

  for (const r of gam) {
    const src = r.validData?.expanded?.creativeSource?.source || null;
    const cp = custParams(r.url || '');
    const hbPassedSlot = !!(cp.hb_bidder || cp.hb_pb || cp.hb_cache_id || cp.amzn_b || cp.amznslots);
    if (hbPassedSlot) hbPassed++;

    const noFillSlot = r.validData?.noFill || src === 'no-fill';
    if (noFillSlot) { noFill++; continue; }

    // Actual RENDERED winner from the creative source the proxy detected. Fall
    // back to the cust_params heuristic only when no source could be attributed.
    let winner;
    if (src === 'prebid') winner = 'prebid';
    else if (src === 'amazon') winner = 'aps';
    else if (src === 'adx') winner = 'adx';
    else if (src === 'direct') winner = 'direct';
    else if (cp.hb_bidder || cp.hb_pb || cp.hb_cache_id) winner = 'prebid';
    else if (cp.amzn_b || cp.amznslots) winner = 'aps';
    else winner = 'adx';

    if (winner === 'prebid') prebidWon++;
    else if (winner === 'aps') apsWon++;
    else if (winner === 'direct') directWon++;
    else adxWon++;

    // HB bid was passed but a non-HB creative rendered → HB lost the GAM auction.
    if (hbPassedSlot && (winner === 'adx' || winner === 'direct')) hbPassedLost++;
  }

  // Prebid bid objects returned (per-bidder results live in the PBS seatbid).
  let prebidBids = 0;
  let prebidAuctionsWithBids = 0;
  for (const r of prebid) {
    const b = typeof r.responseBody === 'string' ? r.responseBody : '';
    if (b.includes('"seatbid"')) {
      try {
        let n = 0;
        for (const s of JSON.parse(b).seatbid || []) n += (s.bid || []).length;
        prebidBids += n;
        if (n > 0) prebidAuctionsWithBids++;
      } catch {
        /* non-JSON / truncated — ignore */
      }
    }
  }

  const apsBidReqs = aps.filter((r) => /\/e\/msdk\/ads/.test(r.url || ''));
  const apsNoBid = apsBidReqs.filter((r) => Number(r.status) === 204).length;

  const filled = gam.length - noFill;
  const hbWon = prebidWon + apsWon;                          // HB actually rendered
  const hbWinRate = filled > 0 ? hbWon / filled : 0;         // share of filled slots
  const hbRenderRate = hbPassed > 0 ? hbWon / hbPassed : 0;  // of bids passed, how many rendered

  // ── Severity ────────────────────────────────────────────────────────────
  let severity;
  let headline;
  if (gam.length === 0) {
    severity = 'info';
    headline = 'No ad requests captured yet';
  } else if (filled === 0) {
    severity = 'warn';
    headline = 'No fills — every ad request came back empty';
  } else if (hbWon === 0) {
    severity = 'fail';
    headline = `Header bidding won 0 of ${filled} filled slots — Google AdX swept everything`;
  } else if (hbWinRate < 0.15) {
    severity = 'warn';
    headline = `Header bidding won only ${Math.round(hbWinRate * 100)}% of filled slots`;
  } else {
    severity = 'pass';
    headline = `Header bidding healthy — won ${Math.round(hbWinRate * 100)}% of filled slots`;
  }

  // ── Flags (plain-language, actionable) ──────────────────────────────────
  const flags = [];
  if (hbWon === 0 && filled > 0) {
    flags.push(
      'All filled slots went to Google AdX; Prebid and APS won nothing. If the device ' +
        'is behind the MITM proxy, the datacenter egress IP can suppress SSP bids — ' +
        'confirm with an off-proxy capture before treating this as a production fill issue.'
    );
  }
  if (apsBidReqs.length > 0 && apsNoBid === apsBidReqs.length) {
    flags.push(`Amazon APS: ${apsNoBid}/${apsBidReqs.length} bid requests returned 204 (no bid).`);
  }
  if (prebid.length > 0 && prebidBids === 0) {
    flags.push(`Prebid: ${prebid.length} auctions ran but 0 bids were returned.`);
  }
  if (hbPassed > 0 && hbPassedLost > 0) {
    flags.push(
      `HB bid passed to GAM on ${hbPassed} slot(s) but rendered on only ${hbWon} — ` +
      `${hbPassedLost} lost to AdX/direct in the GAM auction (bid ≠ render).`
    );
  }

  const pct = (won, total) => (total > 0 ? Math.round((won / total) * 100) : 0);

  // Per-source scoreboard: total participation vs slots won, out of filled inventory.
  const sources = [
    {
      name: 'Prebid',
      total: prebid.length, // auctions run
      totalLabel: `${prebid.length} auctions`,
      responded: prebidAuctionsWithBids, // auctions that returned ≥1 bid
      won: prebidWon,
      ofFilled: filled,
      winRatePct: pct(prebidWon, filled),
    },
    {
      name: 'Amazon APS',
      total: apsBidReqs.length, // bid requests sent
      totalLabel: `${apsBidReqs.length} requests`,
      responded: apsBidReqs.length - apsNoBid, // non-204 responses
      won: apsWon,
      ofFilled: filled,
      winRatePct: pct(apsWon, filled),
    },
    {
      name: 'Google AdX',
      total: filled, // contests every filled slot by default
      totalLabel: 'default fill',
      responded: adxWon,
      won: adxWon,
      ofFilled: filled,
      winRatePct: pct(adxWon, filled),
    },
  ];

  return {
    severity, // 'pass' | 'warn' | 'fail' | 'info'
    headline,
    gamRequests: gam.length,
    filled,
    noFill,
    winMix: { prebid: prebidWon, aps: apsWon, adx: adxWon, direct: directWon },
    prebid: { auctions: prebid.length, bids: prebidBids, auctionsWithBids: prebidAuctionsWithBids, won: prebidWon },
    aps: { bidRequests: apsBidReqs.length, noBid: apsNoBid, won: apsWon },
    sources, // total-vs-won scoreboard per demand source
    hbWinRatePct: Math.round(hbWinRate * 100),
    // Honest bid→render funnel: of the slots where an HB bid was PASSED to GAM,
    // how many actually RENDERED an HB creative vs lost to AdX/direct.
    hbPassed,
    hbRendered: hbWon,
    hbPassedLost,
    hbRenderRatePct: Math.round(hbRenderRate * 100),
    flags,
  };
}

// Multi-line "won / total" scoreboard for display.
export function bidHealthScoreboard(h) {
  const lines = [`${h.gamRequests} ad requests → ${h.filled} filled, ${h.noFill} no-fill`];
  for (const s of h.sources) {
    lines.push(
      `  ${s.name.padEnd(11)} won ${s.won}/${s.ofFilled} (${s.winRatePct}%) · ${s.totalLabel}`
    );
  }
  return lines.join('\n');
}

// One-line headline for compact display, e.g. "30 reqs · PB 2 · APS 0 · AdX 28".
export function bidHealthBadge(h) {
  return `${h.gamRequests} reqs · PB ${h.winMix.prebid} · APS ${h.winMix.aps} · AdX ${h.winMix.adx}`;
}

// ── Platform / app segmentation ───────────────────────────────────────────
// Lets you say "iOS has a problem, not Android" for a specific app, instead of
// reading one blended number. Platform comes from platformClass/devicePlatform
// (present on every row, incl. Prebid/APS), with the GAM `iu` as a fallback.

export function platformOf(r) {
  const s = `${r.platformClass || ''} ${r.devicePlatform || ''} ${r.platform || ''}`.toLowerCase();
  if (s.includes('ios') || s.includes('iphone') || s.includes('ipad')) return 'iOS';
  if (s.includes('android')) return 'Android';
  // fallback: GAM/DAI iu encodes platform (…/iosmob/…, …/droidmob/…, …/iphone/…)
  const iu = String(r.validData?.adUnit || r.id || '').toLowerCase();
  if (/\/(ios(mob|tab)?|iphone|ipad|ipod|appletv|tvos)\//.test(iu)) return 'iOS';
  if (/\/(droid(mob|tab)?|android|firetv)\//.test(iu)) return 'Android';
  return 'Other';
}

// App/brand from a GAM iu: /networkId/<brand>/<platform>/… → brand (e.g. "generic").
export function appOf(r) {
  const parts = String(r.validData?.adUnit || r.id || '').split('/').filter(Boolean);
  return parts.length >= 2 ? parts[1] : null;
}

// Group rows by an arbitrary key and compute bid health per group.
export function computeBidHealthSegmented(rows = [], keyFn) {
  const groups = new Map();
  for (const r of rows) {
    const k = keyFn(r) || 'Unknown';
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  return [...groups.entries()].map(([key, groupRows]) => {
    const health = computeBidHealth(groupRows);
    // apps seen in this segment (from GAM rows)
    const apps = [...new Set(groupRows.map(appOf).filter(Boolean))];
    return { key, apps, ...health };
  });
}

// Convenience: one row per platform (iOS / Android), most ad requests first.
export function computeBidHealthByPlatform(rows = []) {
  return computeBidHealthSegmented(rows, platformOf)
    .filter((s) => s.gamRequests > 0 || s.prebid.auctions > 0 || s.aps.bidRequests > 0)
    .sort((a, b) => b.gamRequests - a.gamRequests);
}

// Convenience: one row per app+platform, e.g. "fnc · iOS". Only segments that
// actually issued GAM ad requests (drops stray non-publisher iu noise).
//
// Prebid/APS requests carry no `iu`, so they can't name their app directly. We
// attribute them to the dominant GAM app on their platform — exact when a capture
// is one app per platform (the normal QA case), approximate for mixed captures.
export function computeBidHealthByAppPlatform(rows = []) {
  const gamCountByPlatformApp = {};
  for (const r of rows) {
    if (rowType(r) !== 'google') continue;
    const app = appOf(r);
    if (!app) continue;
    const p = platformOf(r);
    (gamCountByPlatformApp[p] ||= {})[app] = (gamCountByPlatformApp[p]?.[app] || 0) + 1;
  }
  const dominantApp = {};
  for (const p of Object.keys(gamCountByPlatformApp)) {
    dominantApp[p] = Object.entries(gamCountByPlatformApp[p]).sort((a, b) => b[1] - a[1])[0][0];
  }
  return computeBidHealthSegmented(rows, (r) => {
    const p = platformOf(r);
    const app = appOf(r) || dominantApp[p] || '?';
    return `${app} · ${p}`;
  })
    .filter((s) => s.gamRequests > 0)
    .sort((a, b) => b.gamRequests - a.gamRequests);
}

// ── Video bid health ─────────────────────────────────────────────────────────
// Per app · platform across video sessions: fill, completion, SpringServe/APS
// bid rates, and SpringServe auction win share (SS is the header-bidding layer
// for SSAI video). Mirrors computeBidHealthByAppPlatform for the Video tab.
export function computeVideoBidHealth(sessions = []) {
  const QORD = ['start', 'firstquartile', 'midpoint', 'thirdquartile', 'complete'];
  const canonQ = (b) => QORD.filter((q) => b?.firedQuartiles?.has?.(q)).length;

  const keyOf = (s) => {
    const row = s.imaRow || s.ssC2sRow || s.amazonRow || (s.adBreaks && s.adBreaks[0] && s.adBreaks[0].row) || null;
    const synthetic = { validData: { adUnit: s.displayTitle || s.adUnit || '' } };
    const plat = row && platformOf(row) !== 'Other' ? platformOf(row) : platformOf(synthetic);
    const app = (row && appOf(row)) || appOf(synthetic) || 'video';
    return `${app} · ${plat}`;
  };

  const groups = new Map();
  for (const s of sessions) {
    const key = keyOf(s);
    const g = groups.get(key) || {
      key, sessions: 0, filled: 0, completed: 0, ssBid: 0, apsBid: 0,
      viewable: 0, breaks: 0, filledBreaks: 0, ssWin: 0, daiOnly: 0,
      ssRendered: 0,
    };
    g.sessions += 1;
    const breaks = s.adBreaks || [];
    const filledBreaks = breaks.filter((b) => b.filled !== false);
    g.breaks += breaks.length;
    g.filledBreaks += filledBreaks.length;
    if (filledBreaks.length) g.filled += 1;
    const best = filledBreaks.reduce((a, b) => (canonQ(b) > canonQ(a) ? b : a), filledBreaks[0] || null);
    if (best && best.firedQuartiles && best.firedQuartiles.has('complete')) g.completed += 1;
    const ssBidThis = !!(s.bids && s.bids.ss && s.bids.ss.status === 'bid');
    if (ssBidThis) g.ssBid += 1;
    if (s.bids && s.bids.amazon && s.bids.amazon.status === 'bid') g.apsBid += 1;
    if (s.viewability && s.viewability.present) g.viewable += 1;
    // SS "rendered" = SS bid AND a filled break carries SS auction attribution
    // (creativeChain / dealId / seat / advertiser / bidPrice from SS tracking
    // beacons). If SS bid but nothing rendered, it was outbid by GAM demand or
    // the break didn't fill — that's the real win/loss the header should show.
    const ssRenderedThis = ssBidThis && filledBreaks.some(
      (b) => b.creativeChain || b.dealId || b.seat || b.advertiser || b.bidPrice != null
    );
    if (ssRenderedThis) g.ssRendered += 1;
    for (const b of filledBreaks) {
      if (b.advertiser || b.bidPrice != null || b.creativeChain || b.dealId) g.ssWin += 1;
      else g.daiOnly += 1;
    }
    groups.set(key, g);
  }

  return [...groups.values()]
    .map((g) => {
      const fillRate = g.sessions ? Math.round((g.filled / g.sessions) * 100) : 0;
      const completionRate = g.filled ? Math.round((g.completed / g.filled) * 100) : 0;
      const ssBidRate = g.sessions ? Math.round((g.ssBid / g.sessions) * 100) : 0;
      const apsBidRate = g.sessions ? Math.round((g.apsBid / g.sessions) * 100) : 0;
      // SS win rate = of the times SS BID, how often an SS ad actually rendered.
      // (Previously divided by filled breaks, which ignored every SS-bid-but-no-ad
      //  session and falsely showed 100%.)
      const ssWinRate = g.ssBid ? Math.round((g.ssRendered / g.ssBid) * 100) : 0;
      const ssOutbid = Math.max(0, g.ssBid - g.ssRendered);
      const viewableRate = g.sessions ? Math.round((g.viewable / g.sessions) * 100) : 0;

      const flags = [];
      let severity = 'pass';
      if (g.filled === 0 && g.sessions > 0) { flags.push('No ad filled in any session'); severity = 'fail'; }
      if (g.ssBid === 0 && g.sessions > 0) { flags.push('SpringServe never returned a bid'); if (severity !== 'fail') severity = 'warn'; }
      if (g.ssBid > 0 && ssOutbid > 0) {
        flags.push(`SS bid ${g.ssBid}× but rendered only ${g.ssRendered}× — outbid/no-fill ${ssOutbid}×`);
        if (ssWinRate < 50 && severity === 'pass') severity = 'warn';
      }
      if (fillRate < 50 && g.sessions > 1) { flags.push(`Low fill (${fillRate}%)`); if (severity === 'pass') severity = 'warn'; }
      if (g.filled > 0 && completionRate < 50) { flags.push(`Low completion (${completionRate}%)`); if (severity === 'pass') severity = 'warn'; }

      const headline = `${g.key}: ${g.filled}/${g.sessions} filled · ${completionRate}% completion · SS bid ${ssBidRate}% (${g.ssBid}/${g.sessions}) · APS bid ${apsBidRate}% · SS rendered ${ssWinRate}% (${g.ssRendered}/${g.ssBid})`;
      return { ...g, fillRate, completionRate, ssBidRate, apsBidRate, ssWinRate, ssOutbid, viewableRate, severity, flags, headline };
    })
    .filter((g) => g.sessions > 0)
    .sort((a, b) => b.sessions - a.sessions);
}

export default computeBidHealth;
